import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/button';
import { extractApiError } from '../../../api/clusters';

export interface NewBriefPayload {
    name: string;
    brief_text: string;
    tags: string[];
}

interface NewBriefModalInitialBrief {
    name: string;
    brief_text: string;
    tags: string[];
}

interface NewBriefModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: NewBriefPayload) => Promise<void> | void;
    initialBrief?: NewBriefModalInitialBrief | null;
}

const parseTags = (raw: string): string[] => {
    return raw
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
};

const NewBriefModal: React.FC<NewBriefModalProps> = ({ isOpen, onClose, onSubmit, initialBrief }) => {
    const [name, setName] = useState('');
    const [briefText, setBriefText] = useState('');
    const [tagsRaw, setTagsRaw] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isEditing = Boolean(initialBrief);

    useEffect(() => {
        if (isOpen) {
            setName(initialBrief?.name ?? '');
            setBriefText(initialBrief?.brief_text ?? '');
            setTagsRaw(initialBrief?.tags.join(', ') ?? '');
            setError(null);
            setSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedName = name.trim();
        const trimmedText = briefText.trim();
        if (!trimmedName) {
            setError('Name is required');
            return;
        }
        if (!trimmedText) {
            setError('Brief text is required');
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await onSubmit({
                name: trimmedName,
                brief_text: trimmedText,
                tags: parseTags(tagsRaw),
            });
            onClose();
        } catch (err: any) {
            setError(extractApiError(err, 'Failed to save brief'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-brief-modal-title"
            data-testid="new-brief-modal"
        >
            <div className="bg-surface rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b border-line">
                    <h2
                        id="new-brief-modal-title"
                        className="text-lg font-semibold text-ink"
                    >
                        {isEditing ? 'Edit Brief' : 'Create Brief'}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-ink-muted hover:text-ink"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    <div>
                        <label
                            htmlFor="new-brief-name"
                            className="block text-sm font-medium text-ink mb-1"
                        >
                            Name
                        </label>
                        <input
                            id="new-brief-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={255}
                            className="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                            placeholder="Spring 2026 launch"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="new-brief-text"
                            className="block text-sm font-medium text-ink mb-1"
                        >
                            Brief text
                        </label>
                        <textarea
                            id="new-brief-text"
                            value={briefText}
                            onChange={(e) => setBriefText(e.target.value)}
                            rows={6}
                            maxLength={16000}
                            className="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                            placeholder="Paste your brand brief here…"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="new-brief-tags"
                            className="block text-sm font-medium text-ink mb-1"
                        >
                            Tags (comma-separated, optional)
                        </label>
                        <input
                            id="new-brief-tags"
                            type="text"
                            value={tagsRaw}
                            onChange={(e) => setTagsRaw(e.target.value)}
                            className="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                            placeholder="auto, electric, launch"
                        />
                    </div>

                    {error && (
                        <div
                            className="text-sm text-risk"
                            role="alert"
                            data-testid="new-brief-modal-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting}>
                            {submitting
                                ? 'Saving…'
                                : isEditing
                                    ? 'Save changes'
                                    : 'Save brief'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewBriefModal;
